import type { ElementType, MouseEvent, ReactNode } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

/**
 * LeftNavigation
 * Classification: custom
 *
 * Domain-specific primary side navigation. Built on MUI List primitives but
 * encapsulates product behavior (selected state, collapsed/icon-only mode,
 * grouping) that MUI does not provide out of the box.
 */
export interface NavItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /**
   * Destination. When set, the item renders as a link (an `<a>`, or whatever
   * `linkComponent` is) rather than a button, so the browser's link
   * affordances - new-tab clicks, the context menu, prefetch - all work.
   */
  href?: string;
  /** Forwarded to the rendered element, e.g. `target="_blank"` for an external item. */
  target?: string;
  rel?: string;
}

export interface LeftNavigationProps {
  items: NavItem[];
  selectedId?: string;
  /**
   * Notified when an item is activated. With `href` set this is a
   * notification only (analytics, closing a mobile drawer) - the link performs
   * the navigation - and it does not fire on modified clicks.
   */
  onSelect?: (id: string) => void;
  collapsed?: boolean;
  /** Optional brand / header slot shown above the items. */
  header?: ReactNode;
  width?: number;
  collapsedWidth?: number;
  /**
   * Component used to render items that have an `href`. Defaults to `'a'`.
   * Router-aware apps pass their own link (`next/link`, react-router's `Link`)
   * so client-side navigation and prefetch apply.
   */
  linkComponent?: ElementType;
}

export function LeftNavigation({
  items,
  selectedId,
  onSelect,
  collapsed = false,
  header,
  width = 248,
  collapsedWidth = 64,
  linkComponent = 'a',
}: LeftNavigationProps) {
  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        width: collapsed ? collapsedWidth : width,
        flexShrink: 0,
        height: '100%',
        bgcolor: 'background.default',
        borderRight: 1,
        borderColor: 'divider',
        transition: 'width 200ms ease',
        overflowX: 'hidden',
      }}
    >
      {header ? <Box sx={{ p: collapsed ? 1 : 2 }}>{header}</Box> : null}
      <List disablePadding sx={{ px: 1, py: 1 }}>
        {items.map((item) => {
          const selected = item.id === selectedId;
          const isLink = item.href !== undefined;
          const row = (
            <ListItemButton
              key={item.id}
              selected={selected}
              {...(isLink
                ? {
                    component: linkComponent,
                    href: item.href,
                    target: item.target,
                    rel: item.rel,
                    'aria-current': selected ? ('page' as const) : undefined,
                  }
                : {})}
              onClick={(event: MouseEvent<HTMLElement>) => {
                // A modified click belongs to the browser: calling back here
                // would navigate this tab as well as opening the new one.
                if (isLink && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
                  return;
                }
                onSelect?.(item.id);
              }}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                minHeight: 44,
                justifyContent: collapsed ? 'center' : 'flex-start',
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': { color: 'primary.main' },
                },
              }}
            >
              {item.icon ? (
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
              ) : null}
              {!collapsed ? <ListItemText primary={item.label} /> : null}
            </ListItemButton>
          );
          return collapsed ? (
            <Tooltip key={item.id} title={item.label} placement="right">
              {row}
            </Tooltip>
          ) : (
            row
          );
        })}
      </List>
    </Box>
  );
}
